import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, ThemeProvider, DefaultTheme } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Header, Hero, Features, Terminal, Footer } from './components';
import ProjectsSection from './components/MusicSection';
import { StyledProps } from './types/styled-utils';

// Define theme
const theme: DefaultTheme = {
  colors: {
    primary: '#6E56CF', // Purple primary color similar to Warp
    secondary: '#4CC38A', // Green accent
    darkBg: '#151528', // Dark background
    darkBg2: '#1C1C38', // Slightly lighter dark background
    lightText: '#FFFFFF',
    secondaryText: '#C7D3EB',
    terminalBg: 'rgba(0, 0, 0, 0.7)', // Transparent terminal background
    gradientStart: '#6E56CF',
    gradientEnd: '#4CC38A',
  },
  fonts: {
    primary: "'Inter', sans-serif",
  },
  breakpoints: {
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
};

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: ${({ theme }) => theme.fonts.primary};
    background-color: ${({ theme }) => theme.colors.darkBg};
    color: ${({ theme }) => theme.colors.lightText};
    overflow-x: hidden;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
  }
` as any;

// Styled components
const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const BackgroundGradient = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle at top right,
    rgba(110, 86, 207, 0.1) 0%,
    rgba(28, 28, 56, 0) 70%
  ), 
  radial-gradient(
    circle at bottom left,
    rgba(76, 195, 138, 0.05) 0%,
    rgba(28, 28, 56, 0) 70%
  );
  z-index: -2; /* Ensure the particles are in front of the background gradient */
`;

const ParticlesCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
  opacity: 0.7;
`;

const App: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Simulate loading
  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 1000);
  }, []);

  // Set up particle system (like the one in Warp)
  useEffect(() => {
    const canvas = document.getElementById('particles-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const particles: {
      x: number;
      y: number;
      radius: number;
      speed: number;
      directionX: number;
      directionY: number;
      color: string;
      alpha: number;
    }[] = [];

    const createParticles = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      // Clear existing particles
      particles.length = 0;

      // Create new particles - increase the number from 50 to 150
      for (let i = 0; i < 150; i++) {
        const radius = Math.random() * 2.5 + 0.5; // Slightly larger particles
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const speed = Math.random() * 0.3 + 0.1; // Slightly faster movement
        const directionX = Math.random() * 2 - 1;
        const directionY = Math.random() * 2 - 1;
        const color = theme.colors.primary;
        const alpha = Math.random() * 0.7 + 0.2; // Increase alpha for better visibility

        particles.push({
          x,
          y,
          radius,
          speed,
          directionX,
          directionY,
          color,
          alpha,
        });
      }
    };

    const animate = () => {
      requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const particle of particles) {
        // Update position
        particle.x += particle.directionX * particle.speed;
        particle.y += particle.directionY * particle.speed;

        // Bounce off edges
        if (particle.x > canvas.width || particle.x < 0) {
          particle.directionX *= -1;
        }
        if (particle.y > canvas.height || particle.y < 0) {
          particle.directionY *= -1;
        }

        // Draw particle
        ctx.globalAlpha = particle.alpha;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      }

      // Draw connections between nearby particles
      ctx.globalAlpha = 0.15; // Slightly more visible connections
      ctx.strokeStyle = theme.colors.primary;
      ctx.lineWidth = 0.3;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Increase connection distance from 150 to 180
          if (distance < 180) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    // Handle window resize
    const handleResize = () => {
      createParticles();
    };

    window.addEventListener('resize', handleResize);
    createParticles();
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AppContainer>
        <BackgroundGradient />
        <ParticlesCanvas id="particles-canvas" />
        
        <AnimatePresence>
          {isLoaded ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <Header />
              <Hero />
              <Terminal />
              <Features />
              <ProjectsSection />
              <Footer />
            </motion.div>
          ) : (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
              }}
            >
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <svg width="60" height="60" viewBox="0 0 24 24" fill={theme.colors.primary}>
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm0 22c-5.5 0-10-4.5-10-10S6.5 2 12 2s10 4.5 10 10-4.5 10-10 10z" />
                  <path d="M12 4v8l6 4-1 1.5-7-4.5V4h2z" />
                </svg>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </AppContainer>
    </ThemeProvider>
  );
};

export default App; 